import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  ClickAwayListener,
  Fade,
  Grid2,
  Popper,
  Typography,
  styled,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@emotion/react";
import { useDebouncedCallback } from "use-debounce";
import { SearchField } from "@components/common/forms/SearchField";
import { groupBankByName, GroupedBank } from "@components/BankSelect";
import { LoadingSpinner } from "@components/common";
import { BankOption, BankOptionSmall } from "../BankOption";
import { useBanks } from "../hooks";
import banksList, { WhitelistBank } from "../banksList";

const Container = styled(Box)`
  max-width: 736px;
  width: 100%;
  position: relative;
`;

const PopperInnerWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.border.secondary}`,
  boxShadow: theme.shadows[3],
  borderRadius: theme.shape.borderRadius,
  overflowY: "auto",
  marginTop: theme.spacing(1),
}));

export interface BankSelectProps {
  onChange?: (value: string) => void;
  onSelect?: (groupedBank: GroupedBank) => void;
  banks: GroupedBank[];
  searchValue?: string;
  isFetching?: boolean;
  className?: string;
  shouldDisplayBankList?: boolean;
  shouldDisplayTitle?: boolean;
  controlled?: boolean;
}

export const BankSelectView = ({
  onChange,
  onSelect,
  searchValue,
  banks = [],
  isFetching = false,
  shouldDisplayBankList = true,
  shouldDisplayTitle = true,
  className,
  controlled,
}: BankSelectProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const { t } = useTranslation();
  const [loadingBank, setLoadingBank] = useState<string>();
  const { mutate: getBanks } = useBanks();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isBankSelectOpen, setIsBankSelectOpen] = useState(false);
  const [inputValue, setInputValue] = useState(searchValue || "");

  const handleChange = useDebouncedCallback((value: string) => {
    onChange && onChange(value);
  }, 500);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (controlled) {
        onChange?.(event.target.value);
        setIsBankSelectOpen(true);
      } else {
        setInputValue(event.target.value);
        handleChange(event.target.value);
      }
    },
    [handleChange, controlled, onChange]
  );

  const handleBankSelect = useCallback(
    (bank: WhitelistBank) => {
      setLoadingBank(bank.search);
      getBanks(
        {
          search: bank.search,
        },
        {
          onSuccess: (result) => {
            let banks = result.banks;

            if (bank.filter) {
              banks = banks?.filter(bank.filter);
            }
            setLoadingBank(undefined);
            onSelect?.(groupBankByName(banks)[0]);
          },
        }
      );
    },
    [onSelect, getBanks]
  );

  useEffect(() => {
    if (controlled) return;
    if (searchValue) {
      // this is to prevent popper appearing in the wrong place when animating
      setTimeout(() => {
        setIsBankSelectOpen(true);
      }, 300);
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsBankSelectOpen(false);
    }
  }, [searchValue, controlled]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAnchorEl(ref?.current);
  }, [ref]);

  return (
    <Container data-testid="bank-select-view" className={className}>
      {shouldDisplayTitle && (
        <Box mb={6}>
          <Typography variant="h4">{t("bankSelect.pageTitle")}</Typography>
          <Typography variant="body1" color="textSecondary">
            {t("bankSelect.title")}
          </Typography>
        </Box>
      )}

      <SearchField
        ref={ref}
        value={controlled ? searchValue : inputValue}
        inputProps={{ "data-testid": "bank-search-input" }}
        variant="outlined"
        onChange={handleInputChange}
        InputProps={{
          endAdornment: isFetching && <LoadingSpinner width={20} />,
        }}
        placeholder={t("bankSelect.searchLabel")}
        sx={{
          width: "100%",
          marginBottom: theme.spacing(6),
        }}
        size="small"
        autoFocus
      />

      {anchorEl && (
        <Popper
          id="bank-select"
          data-testid="bank-select"
          open={isBankSelectOpen}
          anchorEl={anchorEl}
          placement="bottom"
          transition
          disablePortal
          sx={{
            display: "flex",
            width: "100%",
            maxHeight: shouldDisplayBankList ? "100%" : "400px",
          }}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener
              onClickAway={(event) => {
                if (
                  !(event.target as HTMLElement).closest(
                    "[data-testid='bank-search-input']"
                  )
                ) {
                  setIsBankSelectOpen(false);
                }
              }}
            >
              <Fade {...TransitionProps}>
                <PopperInnerWrapper p={3} maxHeight="100%" width="100%">
                  <Grid2 container rowSpacing={4} columnSpacing={2}>
                    {banks.map((bank) => (
                      <BankOption
                        key={bank.name}
                        bank={bank}
                        onClick={() => {
                          setIsBankSelectOpen(false);
                          onSelect?.(bank);
                        }}
                        searchValue={searchValue}
                      />
                    ))}
                  </Grid2>

                  {!banks.length && !isFetching && (
                    <Typography variant="body1" mt={4} ml={2}>
                      {t("bankSelect.notFound")}
                    </Typography>
                  )}
                </PopperInnerWrapper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      )}

      {shouldDisplayBankList && (
        <Grid2 container rowSpacing={2.5} columnSpacing={4}>
          {banksList.map((bank) => (
            <BankOptionSmall
              bank={bank}
              isFetching={bank.search === loadingBank}
              onClick={handleBankSelect}
              key={bank.name}
            />
          ))}
        </Grid2>
      )}
    </Container>
  );
};
